// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Design tokens for EAI administration experiences.
 *
 * Tokens are semantic, not decorative: consumers reference `surface`,
 * `onSurface`, `danger` and similar roles rather than raw palette entries, so
 * a brand change never requires editing feature code
 * (`docs/05-CODING-STANDARDS.md` §26, approved design-system components).
 *
 * Colour values are chosen so every documented foreground/background pair meets
 * the WCAG 2.2 Level AA threshold recorded in {@link ./contrast}. ADR-0042
 * makes that target mandatory, and the package test suite asserts each pair.
 *
 * Status colour is never the only carrier of meaning: every component that uses
 * `status` also renders text and an accessible name
 * (`docs/05-CODING-STANDARDS.md` §26, "avoid color-only meaning").
 */

/** Semantic colour roles for one theme mode. */
export interface ColorScheme {
  /** Page background. */
  readonly background: string;
  /** Raised surface such as a card or table. */
  readonly surface: string;
  /** Text and icons drawn on {@link ColorScheme.surface}. */
  readonly onSurface: string;
  /** Lower-emphasis text on {@link ColorScheme.surface}. */
  readonly onSurfaceMuted: string;
  /** Primary action colour used as a background. */
  readonly primary: string;
  /** Text drawn on {@link ColorScheme.primary}. */
  readonly onPrimary: string;
  /** Destructive or error colour used as a background. */
  readonly danger: string;
  /** Text drawn on {@link ColorScheme.danger}. */
  readonly onDanger: string;
  /** Caution colour used as a background. */
  readonly warning: string;
  /** Text drawn on {@link ColorScheme.warning}. */
  readonly onWarning: string;
  /** Healthy or success colour used as a background. */
  readonly success: string;
  /** Text drawn on {@link ColorScheme.success}. */
  readonly onSuccess: string;
  /** Informational colour used as a background. */
  readonly info: string;
  /** Text drawn on {@link ColorScheme.info}. */
  readonly onInfo: string;
  /** Divider and non-text component border. */
  readonly border: string;
  /** Keyboard focus indicator. */
  readonly focusRing: string;
}

/** Light colour scheme. */
export const lightColors: ColorScheme = {
  background: '#f5f6f8',
  surface: '#ffffff',
  onSurface: '#141a21',
  onSurfaceMuted: '#4a5561',
  primary: '#12457c',
  onPrimary: '#ffffff',
  danger: '#a11212',
  onDanger: '#ffffff',
  warning: '#6b4200',
  onWarning: '#ffffff',
  success: '#136139',
  onSuccess: '#ffffff',
  info: '#1a4f6b',
  onInfo: '#ffffff',
  border: '#6b7682',
  focusRing: '#0b3e73',
};

/** Dark colour scheme. */
export const darkColors: ColorScheme = {
  background: '#10151b',
  surface: '#1a212a',
  onSurface: '#eef2f6',
  onSurfaceMuted: '#b3bec9',
  primary: '#9dc6f5',
  onPrimary: '#0b1b2c',
  danger: '#f5a6a6',
  onDanger: '#2c0b0b',
  warning: '#f0c479',
  onWarning: '#2c1f04',
  success: '#8fd6ab',
  onSuccess: '#062516',
  info: '#9ad2ea',
  onInfo: '#062430',
  border: '#8c98a5',
  focusRing: '#bcd9f8',
};

/**
 * Foreground/background pairs that MUST meet a Level AA threshold.
 *
 * The package test suite iterates this table for both schemes, so adding a
 * role here is enough to bring it under the accessibility gate.
 */
export const contrastContract: readonly {
  readonly name: string;
  readonly foreground: keyof ColorScheme;
  readonly background: keyof ColorScheme;
  readonly minimumRatio: number;
}[] = [
  { name: 'body text', foreground: 'onSurface', background: 'surface', minimumRatio: 4.5 },
  { name: 'muted text', foreground: 'onSurfaceMuted', background: 'surface', minimumRatio: 4.5 },
  { name: 'page text', foreground: 'onSurface', background: 'background', minimumRatio: 4.5 },
  { name: 'primary action', foreground: 'onPrimary', background: 'primary', minimumRatio: 4.5 },
  { name: 'danger', foreground: 'onDanger', background: 'danger', minimumRatio: 4.5 },
  { name: 'warning', foreground: 'onWarning', background: 'warning', minimumRatio: 4.5 },
  { name: 'success', foreground: 'onSuccess', background: 'success', minimumRatio: 4.5 },
  { name: 'info', foreground: 'onInfo', background: 'info', minimumRatio: 4.5 },
  { name: 'border', foreground: 'border', background: 'surface', minimumRatio: 3 },
  { name: 'focus ring', foreground: 'focusRing', background: 'surface', minimumRatio: 3 },
];

/** Base spacing unit in pixels; all spacing is a multiple of this value. */
export const spacingUnit = 8;

/** Named spacing steps expressed in multiples of {@link spacingUnit}. */
export const spacing = {
  none: 0,
  xs: 0.5,
  sm: 1,
  md: 2,
  lg: 3,
  xl: 4,
} as const;

/** Corner radii in pixels. */
export const radii = {
  sm: 4,
  md: 8,
  lg: 16,
} as const;

/**
 * Type scale in `rem`.
 *
 * Values are relative so the browser zoom, reflow, and text-resize criteria in
 * `docs/10-TESTING.md` §30 continue to hold.
 */
export const typeScale = {
  body: 1,
  bodySmall: 0.875,
  heading3: 1.25,
  heading2: 1.5,
  heading1: 2,
} as const;

/** Minimum interactive target size in pixels (WCAG 2.2 target size, minimum). */
export const minimumTargetSizePx = 24;

/** Focus indicator thickness in pixels. */
export const focusRingWidthPx = 3;

/** Approved font stack; no remote font is fetched at runtime. */
export const fontFamily =
  '"Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", system-ui, sans-serif';
